import { fmtCell, isMoneyKey, moneySearchText } from "./columns";
import type { FrameRecords } from "../types/api";

// "Search across all columns" — matched against what the grid actually DISPLAYS.
//
// The raw cell value is not what the reader sees: fmtCell renders an ISO doc_date as
// dd/mm/yyyy and blanks/NaN as an em-dash. Searching the raw values therefore made a
// date typed the way it appears on screen ("30/06/2025") match nothing, which is what
// made this filter look broken. Formatting each cell the same way the table does keeps
// the search honest: if you can read it in the grid, you can find it.
//
// Cheap fast paths first — a non-date string/number cell formats to itself, so only
// date columns and blanks need fmtCell. That matters on a 69k-row Dubai frame, where
// this runs per row per keystroke.
function haystackCell(value: unknown, colKey: string): string {
  if (value === null || value === undefined) return "";
  const t = typeof value;
  if (t === "number" && isMoneyKey(colKey)) return moneySearchText(value as number);
  if ((t === "string" || t === "number") && !colKey.toLowerCase().includes("date")) {
    return String(value);
  }
  const s = fmtCell(value, colKey);
  if (s === "—") return "";
  // A date column carries BOTH forms: the displayed dd/mm/yyyy (what the reader sees
  // and types) and the raw ISO yyyy-mm-dd (what they may copy from an export or the
  // API). Matching only one silently breaks the other, so both are searchable.
  const raw = String(value);
  return s === raw ? s : s + " " + raw;
}

// Per-frame memo of the lower-cased searchable text for each row. Frames are replaced
// wholesale on re-finalize, so WeakMap keying is safe and the cache dies with the frame.
// Without it every keystroke re-formatted every cell of every row.
const HAY_CACHE = new WeakMap<FrameRecords, string[]>();

function haystacks(frame: FrameRecords): string[] {
  let rows = HAY_CACHE.get(frame);
  if (!rows) {
    const cols = frame.columns;
    rows = frame.rows.map((r) => {
      let s = "";
      for (let i = 0; i < cols.length; i++) {
        const cell = haystackCell(r[i], cols[i]);
        if (cell) s += cell + " ";
      }
      return s.toLowerCase();
    });
    HAY_CACHE.set(frame, rows);
  }
  return rows;
}

// Normalise a query for comparison. Trimmed + lower-cased; "" means "no filter".
export function normaliseNeedle(text: string): string {
  return text.trim().toLowerCase();
}

// Does this row (identified by its position in `frame`) contain `needle`? `needle` must
// already be normalised via normaliseNeedle.
export function rowMatchesTextAt(frame: FrameRecords, pos: number, needle: string): boolean {
  return haystacks(frame)[pos]?.includes(needle) ?? false;
}

// Row-array overload for callers iterating rows rather than positions. Falls back to
// formatting on the fly when the row is not part of `frame` (e.g. a derived slice).
export function rowMatchesText(
  frame: FrameRecords,
  row: FrameRecords["rows"][number],
  needle: string,
): boolean {
  const cols = frame.columns;
  for (let i = 0; i < cols.length; i++) {
    const cell = haystackCell(row[i], cols[i]);
    if (cell && cell.toLowerCase().includes(needle)) return true;
  }
  return false;
}

// Per-grid search: only the columns actually RENDERED, formatted as rendered. `cols`
// carries each column's frame index plus its key; a paired grid's keys are composite
// (cri::doc_date), so the side prefix is stripped before the date check — otherwise a
// paired date column would fall through the fast path unformatted and stop matching.
//
// This is the exact oracle. visibleCellsMatchAt below is the cached fast path and must
// agree with it cell for cell; keep them in lockstep.
export function visibleCellsMatch(
  row: FrameRecords["rows"][number] | unknown[],
  cols: { key: string; index: number }[],
  needle: string,
): boolean {
  for (const c of cols) {
    const sep = c.key.indexOf("::");
    const rawKey = sep >= 0 ? c.key.slice(sep + 2) : c.key;
    const cell = haystackCell((row as unknown[])[c.index], rawKey);
    if (cell && cell.toLowerCase().includes(needle)) return true;
  }
  return false;
}

// Cached form of the above, for the grid search boxes — which re-run it over every row
// on EVERY KEYSTROKE. visibleCellsMatch re-formats each cell through fmtCell every time;
// on Dubai's in-period insurer slice (25,017 rows x 52 rendered columns) that measured
// 43.0ms per keystroke. Formatting once and searching the result costs 98.4ms to build
// and 1.8ms per keystroke after — break-even at ~2.3 keystrokes.
//
// SEPARATOR IS LOAD-BEARING. Cells are joined with "\n", and a match can only span two
// cells by crossing that separator — so while the needle carries no newline, a hit in
// the joined string necessarily lies inside ONE cell, which is exactly what the per-cell
// scan answers. A needle that does contain one falls back to the oracle rather than risk
// a cross-cell false positive. (The grid's single-term path cannot normally produce one:
// normaliseNeedle trims, and an interior newline splits the text into 2+ terms, which
// routes to the multi-value matcher instead. The guard costs nothing and does not rely
// on that argument holding.)
//
// ONE entry per frame, keyed on the rendered column set: pinning a column replaces the
// haystack rather than accumulating another, so this cannot grow without bound. It is
// dropped with the frame (WeakMap), which a re-finalize replaces wholesale.
const VIS_HAY = new WeakMap<FrameRecords, { key: string; rows: string[] }>();

// Below this many rows the exact path is already ~3ms, so the build never pays for
// itself — small insurers must not be charged for machinery only the largest ones need.
const VIS_HAY_MIN_ROWS = 2000;

function visibleHaystacks(
  frame: FrameRecords,
  cols: { key: string; index: number }[],
): string[] {
  // JSON so two different column sets cannot fold to the same key. Runs once per
  // haystack build, never per row.
  const key = JSON.stringify(cols.map((c) => [c.key, c.index]));
  const hit = VIS_HAY.get(frame);
  if (hit && hit.key === key) return hit.rows;
  // Strip the paired-grid side prefix once per COLUMN, not once per cell.
  const spec = cols.map((c) => {
    const sep = c.key.indexOf("::");
    return { index: c.index, rawKey: sep >= 0 ? c.key.slice(sep + 2) : c.key };
  });
  const rows: string[] = new Array(frame.rows.length);
  for (let p = 0; p < frame.rows.length; p++) {
    const r = frame.rows[p];
    let s = "";
    for (let i = 0; i < spec.length; i++) {
      const cell = haystackCell(r[spec[i].index], spec[i].rawKey);
      // Empty cells are skipped, exactly as the oracle never tests them.
      if (cell) s = s ? s + "\n" + cell : cell;
    }
    rows[p] = s.toLowerCase();
  }
  VIS_HAY.set(frame, { key, rows });
  return rows;
}

/** A matcher over row POSITIONS in `frame`, value-identical to the oracle.
 *
 *  Built ONCE per scan, like StatementTab::makeRowFilter — resolving the cache (and the
 *  small-frame / newline decisions) per row instead put a JSON.stringify of the whole
 *  column set on the hot path and measured SLOWER than the code it replaced: 97.5ms vs
 *  69.3ms per keystroke on Dubai's 25,017-row slice. Hoisting it is what makes the
 *  cached form actually win. */
export function makeVisibleMatcher(
  frame: FrameRecords,
  cols: { key: string; index: number }[],
  needle: string,
): (pos: number) => boolean {
  if (frame.rows.length < VIS_HAY_MIN_ROWS || needle.includes("\n")) {
    return (pos: number) => visibleCellsMatch(frame.rows[pos], cols, needle);
  }
  const hay = visibleHaystacks(frame, cols);
  return (pos: number) => hay[pos]?.includes(needle) ?? false;
}
